// Akte eines Kundenakten-Ordners (`/alte-kundenakten/:id`), seit 01.10.2026
// ohne Fluent und mit Explorer über die Ablage: Die Dateien liegen im
// Objektspeicher, also lässt sich alles ansehen, bevor man zuweist.
//
// Drei Fälle im Körper:
//  - Ordner zugewiesen und gebucht → die echten Dokumente des Kontakts
//    (DocumentsSection, dort gibt es alles: Vorschau, Umbenennen, Ordner).
//  - Ordner abgelegt (abgelegtAt) → AblageExplorer mit Vorschau aus dem Speicher.
//  - Sonst (Übergang, noch nicht abgelegt) → Dateiliste aus dem Share-Index,
//    ohne Vorschau.

import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { apiFetch } from "../../utils/apiFetch";
import { useSmartBack } from "../../hooks/smartBack";
import { DocumentsSection } from "../DocumentsSection";
import { I } from "../redesign/kvpIcons";
import { Chip } from "../redesign/table";
import { FolderIcon } from "../documents/explorer/fileTypeIcons";
import { ZuweisenPopup } from "./ZuweisenPopup";
import { AblageExplorer } from "./AblageExplorer";
import { KundenaktenIndexExplorer } from "./KundenaktenIndexExplorer";
import { Folder, FolderDetail, alleKontaktNamen, humanSize } from "./types";
import { istZugewiesen } from "./ansicht";
import { akteAblageZiel } from "./akteAblage";
import { VorgaengeEntscheidenPopup } from "./VorgaengeEntscheiden";
import { kundenaktenVorgangService, zuEntscheiden, type KundenaktenVorgangEintrag } from "../../services/kundenaktenVorgangService";

const AlteKundenaktenDetail: React.FC = () => {
  const { id: idStr } = useParams();
  const id = Number(idStr);
  const goBack = useSmartBack("/alte-kundenakten");

  const [data, setData] = useState<FolderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [busy, setBusy] = useState(false);
  // Vorgänge aus VO/VE-Ordnern (C2): Stand je Unterordner, Nachfrage bei Offenem.
  const [vorgaenge, setVorgaenge] = useState<KundenaktenVorgangEintrag[]>([]);
  const [vorgaengeOffen, setVorgaengeOffen] = useState(false);

  const load = useCallback(async () => {
    if (!Number.isFinite(id)) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiFetch<FolderDetail>(`kundenakten-folders/${id}`);
      setData(data);
      if (data.folder.contact?.id && data.folder.abgelegtAt) {
        kundenaktenVorgangService
          .liste(id)
          .then((l) => setVorgaenge(l.eintraege))
          .catch(() => setVorgaenge([]));
      } else {
        setVorgaenge([]);
      }
    } catch {
      setError("Ordner konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleNeeded = useCallback(
    async (needed: boolean) => {
      setBusy(true);
      try {
        await apiFetch<Folder>(`kundenakten-folders/${id}/${needed ? "needed" : "not-needed"}`, { method: "POST" });
        await load();
      } catch {
        /* Zustand bleibt; erneuter Versuch möglich */
      } finally {
        setBusy(false);
      }
    },
    [id, load],
  );

  if (loading || error || !data) {
    return (
      <div className="kvp-rd kvp-rd-table-page">
        <div className="bar">
          <button type="button" className="tbtn" onClick={goBack}>
            {I.back}Zurück
          </button>
        </div>
        <div style={{ padding: "18px 26px" }} className={error ? "ak-fehler" : "ak-muted"}>
          {loading ? "Ordner wird geladen …" : error ?? "Ordner nicht gefunden."}
        </div>
      </div>
    );
  }

  const { folder, files } = data;
  const zugewiesen = istZugewiesen(folder);
  const namen = alleKontaktNamen(folder);
  const gebucht = zugewiesen && !!folder.importedAt && !!folder.contact?.id;
  const dateien = folder.fileCount ?? files.length;
  // Nicht gebucht: Gezogenes geht in die Akte des zugewiesenen Kontakts — ohne
  // Kontakt sagt die Ablagezone, warum es nicht geht (User 02.11.2026).
  const ablageZiel = akteAblageZiel(folder);
  const vgAngelegt = vorgaenge.filter((v) => v.status === "angelegt");
  const vgWartend = vorgaenge.filter((v) => v.status === "gewuenscht");
  const vgFehler = vorgaenge.filter((v) => v.status === "fehler");
  const vgOffen = zuEntscheiden(vorgaenge);

  return (
    <div className="kvp-rd kvp-rd-table-page ak-detail">
      <div className="bar">
        <button type="button" className="tbtn" onClick={goBack}>
          {I.back}Zurück
        </button>
        <div className="ak-head-titel" title={folder.folderName}>
          <FolderIcon size={22} />
          <span>{folder.folderName}</span>
        </div>
        <span className="ak-muted">
          {humanSize(folder.sizeBytes)} · {dateien} Dateien
        </span>
        {folder.notNeeded ? (
          <Chip>nicht benötigt</Chip>
        ) : zugewiesen ? (
          <span title={namen.join(" · ")}>
            <Chip tone="chip-kunde">{namen.length > 1 ? `${namen[0]} +${namen.length - 1}` : namen[0]}</Chip>
          </span>
        ) : (
          <Chip>noch offen</Chip>
        )}
        {folder.importError && (
          <span className="ak-fehler" title={folder.importError}>
            Fehler beim Buchen
          </span>
        )}
        {zugewiesen && !folder.importedAt && !folder.importError && <span className="ak-muted">wird gebucht …</span>}
        {folder.missing && <span className="ak-fehler">Fehlt am Share</span>}
        {vgAngelegt.length > 0 && (
          <span title={vgAngelegt.map((v) => v.titel ?? v.name).join(" · ")}>
            <Chip>{vgAngelegt.length === 1 ? "1 Vorgang" : `${vgAngelegt.length} Vorgänge`}</Chip>
          </span>
        )}
        {vgWartend.length > 0 && (
          <span className="ak-muted" title={vgWartend.map((v) => v.titel ?? v.name).join(" · ")}>
            {vgWartend.length} Vorgang/Vorgänge werden angelegt …
          </span>
        )}
        {vgFehler.length > 0 && (
          <span className="ak-fehler" title={vgFehler.map((v) => `${v.name}: ${v.grund ?? ""}`).join(" · ")}>
            {vgFehler.length} Vorgang/Vorgänge mit Fehler
          </span>
        )}
        {vgOffen.length > 0 && (
          <button type="button" className="ak-rowbtn primary" onClick={() => setVorgaengeOffen(true)}>
            Vorgänge entscheiden ({vgOffen.length})
          </button>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          {!folder.missing && (
            <button type="button" className="tbtn primary" onClick={() => setAssigning(true)} disabled={busy}>
              {zugewiesen ? "Zuweisung ändern" : "Zuweisen"}
            </button>
          )}
          {folder.notNeeded ? (
            <button type="button" className="tbtn" onClick={() => void toggleNeeded(true)} disabled={busy}>
              Wieder benötigt
            </button>
          ) : (
            <button type="button" className="tbtn" onClick={() => void toggleNeeded(false)} disabled={busy}>
              Nicht benötigt
            </button>
          )}
        </div>
      </div>

      <div className="ak-detail-body">
        {gebucht ? (
          <DocumentsSection contactId={folder.contact!.id!} nurAlteAkte />
        ) : folder.abgelegtAt ? (
          <AblageExplorer folderId={folder.id} rootLabel={folder.folderName} ablageZiel={ablageZiel} />
        ) : files.length === 0 ? (
          <div className="ak-muted" style={{ padding: "18px 26px" }}>
            Keine Dateiliste vorhanden (Ordner leer oder noch nicht gescannt).
          </div>
        ) : (
          <KundenaktenIndexExplorer files={files} rootLabel={folder.folderName} ablageZiel={ablageZiel} />
        )}
      </div>

      {assigning && (
        <ZuweisenPopup
          folder={folder}
          onClose={() => setAssigning(false)}
          onAssigned={() => {
            setAssigning(false);
            void load();
          }}
        />
      )}
      {vorgaengeOffen && (
        <VorgaengeEntscheidenPopup
          folderId={folder.id}
          folderName={folder.folderName}
          eintraege={vgOffen}
          onClose={() => setVorgaengeOffen(false)}
          onEntschieden={() => {
            setVorgaengeOffen(false);
            void load();
          }}
        />
      )}
    </div>
  );
};

export default AlteKundenaktenDetail;
